<script setup lang="ts">
/**
 * Shows the privacy banner and settings dialog before and after sign-in.
 * Keeps checkbox edits local until the visitor saves them to the privacy store.
 */
import { nextTick, onMounted, ref, watch } from "vue"
import { usePrivacyStore } from "@/stores/privacyStore"
import { createPrivacyChoices, trackingPurposes } from "@/types/privacyChoices"
import Button from "@/components/ui/Button.vue"

const privacy = usePrivacyStore()
const dialog = ref<HTMLDialogElement>()
const draft = ref(createPrivacyChoices())

async function updateDialog(): Promise<void> {
	await nextTick()
	if (privacy.isSettingsOpen) {
		draft.value = { ...privacy.choices }
		if (!dialog.value?.open) dialog.value?.showModal()
	} else {
		dialog.value?.close()
	}
}

watch(() => privacy.isSettingsOpen, updateDialog)
onMounted(updateDialog)

function closeSettings(): void {
	privacy.isSettingsOpen = false
}
</script>

<template>
	<section v-if="privacy.showBanner" class="privacy-banner" role="region" :aria-label="$t('privacy.title')">
		<div class="copy">
			<h2>{{ $t("privacy.title") }}</h2>
			<p>
				{{ $t("privacy.introduction") }}
				<a href="https://www.landerz.ca/privacypolicy" target="_blank" rel="noopener noreferrer">{{
					$t("privacy.policy")
				}}</a>
			</p>
		</div>
		<div class="actions">
			<Button variant="secondary" @click="privacy.saveChoices(createPrivacyChoices(true))">{{
				$t("privacy.acceptAll")
			}}</Button>
			<Button variant="secondary" @click="privacy.saveChoices(createPrivacyChoices())">{{
				$t("privacy.decline")
			}}</Button>
			<Button variant="secondary" @click="privacy.openSettings()">{{ $t("privacy.changeSettings") }}</Button>
		</div>
		<p v-if="privacy.storageError" class="error" role="alert">{{ $t("privacy.storageError") }}</p>
	</section>

	<dialog
		ref="dialog"
		class="privacy-dialog"
		aria-labelledby="privacy-title"
		aria-describedby="privacy-description"
		@cancel="closeSettings"
		@close="closeSettings"
	>
		<div class="heading">
			<h2 id="privacy-title">{{ $t("privacy.settings") }}</h2>
			<Button variant="ghost" :aria-label="$t('privacy.close')" @click="closeSettings">×</Button>
		</div>
		<p id="privacy-description">{{ $t("privacy.introduction") }}</p>
		<p class="essential">{{ $t("privacy.essential") }}</p>
		<form @submit.prevent="privacy.saveChoices(draft)">
			<label v-for="purpose in trackingPurposes" :key="purpose" class="purpose">
				<div>
					<strong>{{ $t(`privacy.purposes.${purpose}.title`) }}</strong>
					<p>{{ $t(`privacy.purposes.${purpose}.description`) }}</p>
				</div>
				<input v-model="draft[purpose]" type="checkbox" :aria-label="$t(`privacy.purposes.${purpose}.title`)" />
			</label>
			<p class="note">{{ $t("privacy.changeNotice") }}</p>
			<a href="https://www.landerz.ca/privacypolicy" target="_blank" rel="noopener noreferrer">{{
				$t("privacy.policy")
			}}</a>
			<p v-if="privacy.storageError" class="error" role="alert">{{ $t("privacy.storageError") }}</p>
			<div class="actions">
				<Button variant="secondary" @click="privacy.saveChoices(createPrivacyChoices(true))">{{
					$t("privacy.acceptAll")
				}}</Button>
				<Button variant="secondary" @click="privacy.saveChoices(createPrivacyChoices())">{{
					$t("privacy.decline")
				}}</Button>
				<Button type="submit" variant="secondary">{{ $t("privacy.save") }}</Button>
			</div>
		</form>
	</dialog>
</template>

<style scoped>
.privacy-banner,
.privacy-dialog {
	margin: auto;
	color: var(--neutral-900);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	padding: var(--space-24);
	box-shadow: var(--shadow-lg);
	font-size: var(--font-size-base);
	line-height: 1.5;
	h2 {
		margin: 0 0 var(--space-8);
		font-size: var(--font-size-large);
	}
	p {
		margin: 0 0 var(--space-12);
	}
	a {
		color: inherit;
		text-decoration: underline;
	}
	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-8);
	}
	.error {
		margin-top: var(--space-12);
		color: var(--accent-warning);
	}
}
.privacy-banner {
	position: fixed;
	z-index: 10000;
	bottom: var(--space-16);
	left: var(--space-16);
	right: var(--space-16);
	max-width: 1040px;
	margin: auto;
	max-height: calc(100dvh - 32px);
	overflow: auto;
}
.privacy-dialog {
	width: min(640px, calc(100vw - 32px));
	max-height: calc(100dvh - 32px);
	box-sizing: border-box;
	&::backdrop {
		background: rgb(0 0 0 / 45%);
	}
	.heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-16);
	}
	.essential,
	.note {
		color: var(--neutral-700);
	}
	.purpose {
		display: flex;
		align-items: flex-start;
		gap: var(--space-24);
		padding: var(--space-16) 0;
		border-top: var(--border-width-thin) solid var(--neutral-200);
		div {
			flex: 1;
		}
		p {
			margin: var(--space-4) 0 0;
		}
		input {
			flex-shrink: 0;
			width: 20px;
			height: 20px;
			margin-top: var(--space-4);
		}
	}
	.actions {
		margin-top: var(--space-20);
	}
}
@media (max-width: 640px) {
	.privacy-banner,
	.privacy-dialog {
		padding: var(--space-16);
		.actions > * {
			flex: 1 1 100%;
		}
	}
}
</style>
